import { useEffect, useMemo, useState } from 'react';
import type { ArtworkPlacement, MaterialId } from '@shared/rfq';
import { asset } from '@/config';
import { useI18n } from '@/i18n';
import { Link, ROUTES, useRouter } from '@/router';
import { Arrow } from '@/components/SiteChrome';
import { ContactSection } from '@/components/ContactSection';
import { Stage } from '@/configurator/product/Stage';
import { circumference, containersFor, getContainer, type ContainerModel } from '@/configurator/product/model';
import { routeFor } from '@/configurator/registry';
import { createSampleArtwork } from '@/lib/sampleArtwork';

export function HomePage() {
  const { anchor } = useRouter();
  useEffect(() => {
    document.title = 'RDK — цифровий декор скла, кераміки та упаковки';
    if (anchor) requestAnimationFrame(() => document.getElementById(anchor)?.scrollIntoView({ block: 'start' }));
  }, [anchor]);

  return (
    <main id="main" className="home">
      <Hero />
      <Marquee />
      <Technology />
      <Products />
      <Portfolio />
      <B2B />
      <ContactSection />
    </main>
  );
}

// ------------------------------------------------------------------ shared bits
/** Intrinsic sizes of /public/images, so lazy images reserve space. Update when replacing photos. */
const IMG: Record<string, [number, number]> = {
  'hero-candles': [1600, 1689],
  'uv-machine': [1600, 748],
  'candle-frosted': [1557, 1011],
  'candle-frosted-tall': [1272, 1314],
  diffusers: [1600, 1039],
  cosmetics: [1596, 1350],
  bottles: [1263, 1125],
  drinkware: [1400, 1174],
  'gift-box': [1263, 1119],
};
function Img({ name, alt, eager = false }: { name: string; alt: string; eager?: boolean }) {
  const [w, h] = IMG[name] ?? [800, 600];
  return (
    <img
      src={asset(`images/${name}.jpg`)}
      alt={alt}
      width={w}
      height={h}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      {...(eager ? { fetchPriority: 'high' as const } : {})}
    />
  );
}
const artCache = new Map<string, HTMLCanvasElement>();
function sampleArt(v: 'fig' | 'citrus' | 'noir') {
  if (!artCache.has(v)) artCache.set(v, createSampleArtwork(v));
  return artCache.get(v)!;
}

function wrapPlacement(c: ContainerModel): ArtworkPlacement {
  const C = circumference(c);
  return { uMm: C / 2, vMm: c.print.heightMm / 2, widthMm: C, heightMm: C / 4, rotationDeg: 0 };
}

function Crosshairs() {
  return (
    <>
      {(['tl', 'tr', 'bl', 'br'] as const).map((p) => (
        <svg key={p} viewBox="0 0 24 24" className={`xhair xhair--${p}`} aria-hidden="true">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 0v24M0 12h24" />
        </svg>
      ))}
    </>
  );
}

function sendInterest(label: string) {
  window.dispatchEvent(new CustomEvent('rdk:interest', { detail: label }));
}

// ------------------------------------------------------------------ hero
function Hero() {
  const { d } = useI18n();
  const h = d.hero;
  const c = getContainer('classic-80');
  return (
    <section className="hero" aria-labelledby="hero-title">
      <div className="wrap hero__grid">
        <div className="hero__copy">
          <p className="eyebrow eyebrow--dot">{h.eyebrow}</p>
          <h1 id="hero-title" className="display hero__title">
            {h.titleA}{' '}
            <span className="accent-word">
              {h.titleAccent}
              <svg viewBox="0 0 300 18" preserveAspectRatio="none" aria-hidden="true">
                <path d="M3 13 C 70 4, 160 3, 297 9" />
              </svg>
            </span>{' '}
            {h.titleB}
          </h1>
          <p className="hero__lead">{h.lead}</p>
          <div className="btn-row">
            <Link to={ROUTES.candleConfigurator} className="btn btn--primary btn--lg btn--glow">
              {h.cta}
              <span className="btn__bubble">
                <Arrow dir="e" />
              </span>
            </Link>
            <Link to="/#contact" className="btn btn--ghost btn--lg">
              {h.secondary} <Arrow />
            </Link>
          </div>
        </div>

        <figure className="hero__visual">
          <Crosshairs />
          <div className="photo photo--hero">
            <Img name="hero-candles" alt={h.imageAlt} eager />
          </div>
          <Link to={ROUTES.candleConfigurator} className="live-card" aria-label={`${h.liveTitle}. ${h.liveCta}`}>
            <span className="live-card__stage">
              <Stage
                container={c}
                material="frosted"
                ink="cmyk_white"
                image={sampleArt('fig')}
                placement={wrapPlacement(c)}
                view="front"
                viewAngleDeg={180}
                autoRotate={30}
                showGuides={false}
                labels={{ printBand: '', seam: '', stageLabel: h.liveTitle }}
              />
            </span>
            <span className="live-card__body">
              <span className="live-card__kicker mono">
                <i /> Live
              </span>
              <span className="live-card__title">{h.liveTitle}</span>
              <span className="live-card__text">{h.liveText}</span>
              <span className="live-card__cta">
                {h.liveCta} <Arrow dir="e" />
              </span>
            </span>
          </Link>
        </figure>
      </div>

      <div className="wrap">
        <dl className="stats">
          {h.stats.map((s) => (
            <div key={s.v}>
              <dt className="display">{s.v}</dt>
              <dd>{s.k}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

function Marquee() {
  const { d } = useI18n();
  const items = [...d.hero.materials, ...d.hero.materials];
  return (
    <div className="marquee" aria-hidden="true">
      <div className="marquee__track">
        {items.map((m, i) => (
          <span key={i}>
            {m}
            <i className={`dot dot--${['c', 'm', 'y', 'k'][i % 4]}`} />
          </span>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ technology
function Technology() {
  const { d } = useI18n();
  const t = d.uv;
  return (
    <section id="printing" className="section section--dark" aria-labelledby="printing-title">
      <div className="wrap tech">
        <div className="tech__copy">
          <p className="eyebrow">{t.eyebrow}</p>
          <h2 id="printing-title" className="display section__title">
            {t.title}
          </h2>
          <p className="section__lead">{t.lead}</p>
          <ul className="points">
            {t.points.map((p, i) => (
              <li key={p.t}>
                <span className="points__n mono">0{i + 1}</span>
                <h3>{p.t}</h3>
                <p>{p.d}</p>
              </li>
            ))}
          </ul>
        </div>
        <div className="tech__media">
          <figure className="photo photo--framed">
            <Crosshairs />
            <Img name="uv-machine" alt={t.imageAlt} />
          </figure>
          <figure className="layers" aria-label={t.layersTitle}>
            <figcaption className="mono">{t.layersTitle}</figcaption>
            <ol className="layers__stack">
              {t.layers.map((l) => (
                <li key={l.id} className={`layer layer--${l.id}`}>
                  <span className="layer__slab" aria-hidden="true" />
                  <span className="layer__label">
                    <b>{l.label}</b> <span>{l.d}</span>
                  </span>
                </li>
              ))}
            </ol>
          </figure>
        </div>
      </div>
    </section>
  );
}

// ------------------------------------------------------------------ products
type ItemKey = 'diffuser' | 'cosmetic' | 'glass' | 'drinkware' | 'gift';
const ITEMS: { key: ItemKey; id: string; img: string; index: string; route: string }[] = [
  { key: 'diffuser', id: 'diffusers', img: 'diffusers', index: '2.2', route: routeFor('diffuser') },
  { key: 'cosmetic', id: 'cosmetics', img: 'cosmetics', index: '2.3', route: routeFor('cosmetic') },
  { key: 'glass', id: 'glass', img: 'bottles', index: '2.4', route: routeFor('bottle') },
  { key: 'drinkware', id: 'drinkware', img: 'drinkware', index: '2.5', route: routeFor('drinkware') },
  { key: 'gift', id: 'gifts', img: 'gift-box', index: '2.6', route: routeFor('gift') },
];

function Products() {
  const { d } = useI18n();
  const p = d.products;
  return (
    <section id="products" className="section section--paper2" aria-labelledby="candles-title">
      <div className="wrap">
        <CandleFeature />

        <header className="split-head" id="decorate">
          <h2 className="display section__title section__title--xl">{p.title}</h2>
          <p className="section__lead">{p.lead}</p>
        </header>

        <div className="pcards pcards--two">
          {ITEMS.slice(0, 2).map((it) => (
            <ProductCard key={it.key} item={it} />
          ))}
        </div>
        <div className="pcards pcards--three">
          {ITEMS.slice(2).map((it) => (
            <ProductCard key={it.key} item={it} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductCard({ item }: { item: (typeof ITEMS)[number] }) {
  const { d } = useI18n();
  const it = d.products.items[item.key];
  return (
    <article id={item.id} className="pcard" aria-labelledby={`${item.id}-title`}>
      <Link to={item.route} className="photo pcard__photo" aria-label={`${d.products.configure}: ${it.title}`}>
        <Img name={item.img} alt={it.imageAlt} />
        <span className="badge badge--light mono">{item.index}</span>
        <span className="badge badge--live mono">
          <i /> {d.products.soonBadge}
        </span>
      </Link>
      <div className="pcard__head">
        <h3 id={`${item.id}-title`} className="display pcard__title">
          <Link to={item.route}>{it.title}</Link>
        </h3>
        <Link to={item.route} className="round-btn" aria-label={`${d.products.configure}: ${it.title}`}>
          <Arrow />
        </Link>
      </div>
      <p className="pcard__text">{it.text}</p>
      <p className="tags mono">{it.tags.join('   ')}</p>
      <p className="pcard__links">
        <Link to={item.route} className="pcard__cta">
          {d.products.configure} <Arrow dir="e" />
        </Link>
        <Link to="/#contact" className="pcard__alt" onClick={() => sendInterest(it.title)}>
          {d.products.request}
        </Link>
      </p>
    </article>
  );
}

function CandleFeature() {
  const { d } = useI18n();
  const c = d.products.candle;
  const candles = containersFor('candle');
  const [glass, setGlass] = useState<MaterialId>('amber');
  const [containerId, setContainerId] = useState(candles[0].id);
  const container = getContainer(containerId);
  const placement = useMemo(() => wrapPlacement(container), [container]);

  return (
    <article className="feature" aria-labelledby="candles-title" id="candles">
      <div className="feature__copy">
        <p className="eyebrow">
          {d.products.eyebrow} · {c.index}
        </p>
        <h2 id="candles-title" className="display section__title section__title--xl">
          {c.title}
        </h2>
        <p className="feature__text">{c.text}</p>
        <ul className="chips mono">
          {c.chips.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
        <figure className="photo feature__photo">
          <Img name="candle-frosted" alt={c.imageAlt} />
        </figure>
      </div>

      <div className="widget">
        <div className="widget__head">
          <span className="mono">{c.widgetTitle}</span>
          <span className="pill-live mono">{c.widgetLive}</span>
        </div>
        <div className="widget__stage">
          <Stage
            container={container}
            material={glass}
            ink="cmyk_white"
            image={sampleArt('fig')}
            placement={placement}
            view="front"
            viewAngleDeg={180}
            autoRotate={14}
            showGuides={false}
            labels={{ printBand: '', seam: '', stageLabel: `${container.name} · ${d.cfg.materials[glass].label}` }}
          />
        </div>
        <div className="widget__controls">
          <div>
            <p className="kicker">{c.widgetGlass}</p>
            <div className="swatches" role="radiogroup" aria-label={c.widgetGlass}>
              {container.materials.map((g) => (
                <button
                  key={g}
                  type="button"
                  role="radio"
                  aria-checked={g === glass}
                  aria-label={d.cfg.materials[g].label}
                  title={d.cfg.materials[g].label}
                  className={`swatch swatch--${g}`}
                  onClick={() => setGlass(g)}
                />
              ))}
            </div>
            <p className="widget__value">{d.cfg.materials[glass].label}</p>
          </div>
          <div>
            <p className="kicker">{c.widgetContainer}</p>
            <div className="seg-pills" role="radiogroup" aria-label={c.widgetContainer}>
              {candles.map((k) => (
                <button key={k.id} type="button" role="radio" aria-checked={k.id === containerId} onClick={() => setContainerId(k.id)}>
                  {k.name.split(' ')[0]}
                </button>
              ))}
            </div>
          </div>
        </div>
        <Link to={ROUTES.candleConfigurator} className="btn btn--primary btn--lg widget__cta">
          {c.widgetCta}
          <span className="btn__bubble">
            <Arrow dir="e" />
          </span>
        </Link>
      </div>
    </article>
  );
}

// ------------------------------------------------------------------ portfolio
function Portfolio() {
  const { d } = useI18n();
  const p = d.portfolio;
  return (
    <section id="portfolio" className="section section--sheet" aria-labelledby="portfolio-title">
      <div className="wrap folio">
        <header className="folio__intro">
          <p className="eyebrow">{p.eyebrow}</p>
          <h2 id="portfolio-title" className="display section__title section__title--xl">
            {p.title}
          </h2>
          <p className="section__lead">{p.lead}</p>
          <p className="folio__note mono">{p.note}</p>
        </header>
        <ul className="folio__grid">
          {p.items.map((it) => (
            <li key={it.title} className="work">
              <Img name={it.img} alt="" />
              <div className="work__cap">
                <span className="mono">{it.client}</span>
                <h3 className="display">{it.title}</h3>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// ------------------------------------------------------------------ B2B
function B2B() {
  const { d } = useI18n();
  const b = d.b2b;
  return (
    <section id="b2b" className="section section--paper2" aria-labelledby="b2b-title">
      <div className="wrap">
        <header className="split-head">
          <div>
            <p className="eyebrow">{b.eyebrow}</p>
            <h2 id="b2b-title" className="display section__title">
              {b.title}
            </h2>
          </div>
          <p className="section__lead">{b.lead}</p>
        </header>
        <ol className="timeline">
          {b.steps.map((s, i) => (
            <li key={s.t}>
              <span className="timeline__n mono">{String(i + 1).padStart(2, '0')}</span>
              <h3 className="display">{s.t}</h3>
              <p>{s.d}</p>
            </li>
          ))}
        </ol>
        <div className="b2b-panel">
          <ul className="perks">
            {b.perks.map((x) => (
              <li key={x}>
                <svg viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M3 8.5 6.5 12 13 4.5" />
                </svg>
                {x}
              </li>
            ))}
          </ul>
          <div className="cta-panel">
            <p className="display cta-panel__title">{b.ctaTitle}</p>
            <Link to="/#contact" className="btn btn--light btn--lg">
              {b.cta} <Arrow />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
